import { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../components/sections/Navbar";
import PageHeader from "../components/sections/PageHeader";
import Tabs from "../components/sections/Tabs";
import LoonOpZandFilterBar from "../components/sections/LoonOpZandFilterBar";
import Breadcrumb from "../components/ui/Breadcrumb";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import {
  PdfExportProvider,
  PdfExportSection,
  usePdfExport,
} from "../context/PdfExportContext";
import {
  getLoonOpZandComparison,
  getLoonOpZandQuestionnaires,
  getLoonOpZandResults,
} from "../api/loonopzandDashboard";
import LoonOpZandQuestionnaireTab from "./tabs/loonopzand/LoonOpZandQuestionnaireTab";
import LoonOpZandComparisonTab from "./tabs/loonopzand/LoonOpZandComparisonTab";
import LoonOpZandIntroductionTab from "./tabs/loonopzand/LoonOpZandIntroductionTab";
import { getQuestionSummary } from "./tabs/loonopzand/questionSummaries";
import "./LoonOpZandDashboard.css";

const INTRODUCTION_TAB = "introductie";
const RESPONSE_OVERVIEW = {
  value: "overview",
  label: "Responsaantallen",
};

const TOEGANG_11_QUESTIONS = [
  RESPONSE_OVERVIEW,
  { value: "1", label: "1. Wat was de hulpvraag waarvoor u contact heeft opgenomen?" },
  { value: "2", label: "2. Heeft u geprobeerd uw hulpvraag zelf op te lossen?" },
  { value: "3", label: "3. Wat was de belangrijkste reden voor contact?" },
  { value: "4", label: "4. In hoeverre bent u het eens met de volgende stellingen?" },
  { value: "5", label: "5. Hoe bent u met uw vraag bij de gemeente uitgekomen?" },
  { value: "6", label: "6. Heeft u gebruik gemaakt van een onafhankelijke cliëntondersteuner?" },
  { value: "7", label: "7. Wat was de uitkomst van uw hulpvraag?" },
  { value: "8", label: "8. Wat vindt u dat goed gaat? En/of wat had anders of beter gemoeten?" },
  { value: "9", label: "9. Welk rapportcijfer geeft u het contact over uw Wmo aanvraag?" },
];

const TOEGANG_12_QUESTIONS = [
  RESPONSE_OVERVIEW,
  {
    value: "1",
    label: "1. Waarbij heeft de Wmo-ondersteuning u geholpen?",
  },
  {
    value: "2",
    label: "2. Wat is de reden dat u opnieuw een aanvraag doet?",
  },
  {
    value: "3",
    label: "3. Wat is voor u van toepassing?",
  },
  {
    value: "4",
    label: "4. Wat was de hulpvraag waarvoor u deze keer contact heeft opgenomen?",
  },
  {
    value: "5",
    label: "5. In hoeverre bent u het eens met de volgende stellingen?",
  },
  {
    value: "6",
    label: "6. Hoe bent u met uw vraag bij de gemeente uitgekomen?",
  },
  {
    value: "7",
    label: "7. Heeft u gebruik gemaakt van een onafhankelijke cliëntondersteuner?",
  },
  {
    value: "8",
    label: "8. Wat was de uitkomst van uw hulpvraag?",
  },
  {
    value: "9",
    label: "9. Wat vindt u dat goed gaat? En/of wat had anders of beter gemoeten?",
  },
  {
    value: "10",
    label: "10. Welk rapportcijfer geeft u het contact over uw Wmo aanvraag?",
  },
];

const VRAGENLIJST_2_QUESTIONS = [
  RESPONSE_OVERVIEW,
  {
    value: "1",
    label: "1. Heeft u tijdens het gesprek met de toegang informatie gekregen over de wachttijd?",
  },
  {
    value: "2",
    label: "2. Heeft u tijdens het wachten op hulp iets anders geregeld?",
  },
  {
    value: "3",
    label: "3. Verwacht u met de geboden hulp voldoende vooruit te kunnen?",
  },
  {
    value: "4",
    label: "4. Wat vindt u dat goed gaat? En/of wat had anders of beter gemoeten?",
  },
  {
    value: "5",
    label: "5. Welk rapportcijfer geeft u uw hulp of ondersteuning uit de Wmo?",
  },
];

const VRAGENLIJST_3_QUESTIONS = [
  RESPONSE_OVERVIEW,
  {
    value: "1",
    label: "1. Waarbij helpt deze hulp of ondersteuning?",
  },
  {
    value: "2",
    label: "2. Wat vindt u dat goed gaat? En/of wat had anders of beter gemoeten?",
  },
  {
    value: "3",
    label: "3. Welk rapportcijfer geeft u uw hulp of ondersteuning uit de Wmo?",
  },
];

const IMPLEMENTED_QUESTIONNAIRES = new Set([
  "toegang-1-1",
  "toegang-1-2",
  "vragenlijst-2",
  "vragenlijst-3",
]);
const COMPARISON_TAB = "respons-rapportcijfers";

function PlaceholderTab() {
  return (
    <div className="loonopzand-dashboard__placeholder">
      Dashboard wordt toegevoegd
    </div>
  );
}

function waitForExportRender() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(resolve, 100);
      });
    });
  });
}

function QuestionnaireExportSource({
  data,
  mode,
  questionnaireSlug,
  questionOptions,
}) {
  if (!data || !IMPLEMENTED_QUESTIONNAIRES.has(questionnaireSlug)) {
    return null;
  }

  return (
    <div className="loonopzand-dashboard__pdf-source" aria-hidden="true">
      {questionOptions.map((question, index) => (
        <PdfExportSection
          key={question.value}
          sectionKey={`${questionnaireSlug}-${question.value}`}
          order={index * 100}
        >
          <section className="loonopzand-dashboard__pdf-question">
            <LoonOpZandQuestionnaireTab
              data={data}
              mode={mode}
              selectedQuestion={String(question.value)}
              questionnaireSlug={questionnaireSlug}
              exportAll
            />
          </section>
        </PdfExportSection>
      ))}
    </div>
  );
}

function LoonOpZandDashboardContent() {
  const { authorizedFetch } = useAuth();
  const { exportPdf, exportSectionedPdf } = usePdfExport();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [activeQuestionnaire, setActiveQuestionnaire] = useState(INTRODUCTION_TAB);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState("overview");
  const [mode, setMode] = useState("abs");
  const [results, setResults] = useState(null);
  const [isLoadingRegistry, setIsLoadingRegistry] = useState(true);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setIsLoadingRegistry(true);
    getLoonOpZandQuestionnaires(authorizedFetch, { signal: controller.signal })
      .then((payload) => setQuestionnaires(payload.questionnaires || []))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setError(requestError.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingRegistry(false);
        }
      });
    return () => controller.abort();
  }, [authorizedFetch]);

  useEffect(() => {
    if (
      !IMPLEMENTED_QUESTIONNAIRES.has(activeQuestionnaire) &&
      activeQuestionnaire !== COMPARISON_TAB
    ) {
      setIsLoadingResults(false);
      return undefined;
    }
    const controller = new AbortController();
    setIsLoadingResults(true);
    setError("");
    const request = activeQuestionnaire === COMPARISON_TAB
      ? getLoonOpZandComparison(authorizedFetch, { signal: controller.signal })
      : getLoonOpZandResults(
          authorizedFetch,
          activeQuestionnaire,
          selectedIssues,
          { signal: controller.signal }
        );
    request
      .then((payload) => setResults(payload))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setError(requestError.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingResults(false);
        }
      });
    return () => controller.abort();
  }, [activeQuestionnaire, authorizedFetch, selectedIssues]);

  const activeLabel =
    activeQuestionnaire === INTRODUCTION_TAB
      ? "Introductiepagina"
      : activeQuestionnaire === COMPARISON_TAB
      ? "Respons en rapportcijfers"
      : questionnaires.find((item) => item.slug === activeQuestionnaire)?.label ||
        "Toegang 1.1";

  const tabs = useMemo(
    () =>
      [
        {
          key: INTRODUCTION_TAB,
          label: "Introductiepagina",
          Component: LoonOpZandIntroductionTab,
        },
        ...questionnaires.map((questionnaire) => ({
          key: questionnaire.slug,
          label: questionnaire.label,
          Component: IMPLEMENTED_QUESTIONNAIRES.has(questionnaire.slug)
            ? LoonOpZandQuestionnaireTab
            : PlaceholderTab,
        })),
        {
          key: COMPARISON_TAB,
          label: "Respons en rapportcijfers",
          Component: LoonOpZandComparisonTab,
        },
      ],
    [questionnaires]
  );

  const tabProps = useMemo(
    () => ({
      "toegang-1-1": { data: results, mode, selectedQuestion },
      "toegang-1-2": {
        data: results,
        mode,
        selectedQuestion,
        questionnaireSlug: "toegang-1-2",
      },
      "vragenlijst-2": {
        data: results,
        mode,
        selectedQuestion,
        questionnaireSlug: "vragenlijst-2",
      },
      "vragenlijst-3": {
        data: results,
        mode,
        selectedQuestion,
        questionnaireSlug: "vragenlijst-3",
      },
      [COMPARISON_TAB]: { data: results },
    }),
    [results, mode, selectedQuestion]
  );

  const handleTabChange = useCallback((slug) => {
    setActiveQuestionnaire(slug);
    setSelectedIssues([]);
    setSelectedQuestion("overview");
    setResults(null);
    setError("");
  }, []);

  const showFilters = IMPLEMENTED_QUESTIONNAIRES.has(activeQuestionnaire) && results;
  const questionOptions = activeQuestionnaire === "toegang-1-2"
    ? TOEGANG_12_QUESTIONS
    : activeQuestionnaire === "vragenlijst-2"
      ? VRAGENLIJST_2_QUESTIONS
      : activeQuestionnaire === "vragenlijst-3"
        ? VRAGENLIJST_3_QUESTIONS
        : TOEGANG_11_QUESTIONS;

  const handlePdf = useCallback(async () => {
    if (IMPLEMENTED_QUESTIONNAIRES.has(activeQuestionnaire)) {
      setIsPreparingPdf(true);
      try {
        await waitForExportRender();
        await exportSectionedPdf?.({
          title: `Loon op Zand - ${activeLabel}`,
          sections: questionOptions.map((question) => ({
            key: `${activeQuestionnaire}-${question.value}`,
            label: question.label,
            summary: getQuestionSummary(activeQuestionnaire, question.value),
          })),
        });
      } catch (exportError) {
        setError(exportError?.message || "PDF exporteren is mislukt.");
      } finally {
        setIsPreparingPdf(false);
      }
      return;
    }
    exportPdf?.({
      title: `Loon op Zand - ${activeLabel}`,
      metaLines: [
        `Vragenlijst: ${activeLabel}`,
        `Vraag: ${selectedQuestion}`,
      ],
    });
  }, [
    activeLabel,
    activeQuestionnaire,
    exportPdf,
    exportSectionedPdf,
    questionOptions,
    selectedQuestion,
  ]);

  const selectedQuestionIndex = questionOptions.findIndex(
    (question) => String(question.value) === String(selectedQuestion)
  );
  const hasPreviousQuestion = selectedQuestionIndex > 0;
  const hasNextQuestion =
    selectedQuestionIndex >= 0 && selectedQuestionIndex < questionOptions.length - 1;
  const showQuestionNavigation = IMPLEMENTED_QUESTIONNAIRES.has(activeQuestionnaire);

  const selectRelativeQuestion = (offset) => {
    const nextQuestion = questionOptions[selectedQuestionIndex + offset];
    if (nextQuestion) {
      setSelectedQuestion(String(nextQuestion.value));
    }
  };
  const loading = isLoadingRegistry || isLoadingResults;

  return (
    <div>
      <Navbar />
      <main className="container loonopzand-dashboard">
        <Breadcrumb
          icon="/home-line.png"
          items={["Dashboards"]}
          current="Loon op Zand"
        />
        <PageHeader
          title="Loon op Zand – WMO cliëntervaringsonderzoek"
          status={loading ? "Data ophalen..." : null}
          actions={activeQuestionnaire === INTRODUCTION_TAB ? null : (
            <Button
              variant="secondary"
              icon="/icons/download-grijs.png"
              onClick={handlePdf}
              disabled={
                isPreparingPdf ||
                !results ||
                (!IMPLEMENTED_QUESTIONNAIRES.has(activeQuestionnaire) &&
                  activeQuestionnaire !== COMPARISON_TAB)
              }
            >
              {IMPLEMENTED_QUESTIONNAIRES.has(activeQuestionnaire)
                ? isPreparingPdf
                  ? "PDF maken…"
                  : "Exporteer vragenlijst"
                : "Download PDF"}
            </Button>
          )}
        />
        {activeQuestionnaire !== INTRODUCTION_TAB && (
          <p className="loonopzand-dashboard__subtitle">{activeLabel}</p>
        )}

        {showFilters && (
          <LoonOpZandFilterBar
            issues={results.filters?.available_issues || []}
            selectedIssues={selectedIssues}
            onIssuesChange={setSelectedIssues}
            questions={questionOptions}
            selectedQuestion={selectedQuestion}
            onQuestionChange={setSelectedQuestion}
            mode={mode}
            onModeChange={setMode}
          />
        )}

        {error && <div className="loonopzand-dashboard__error">{error}</div>}
        {isLoadingRegistry && (
          <div className="loonopzand-dashboard__loading">Dashboard laden…</div>
        )}
        {!isLoadingRegistry && tabs.length > 0 && (
          <Tabs
            tabs={tabs}
            activeKey={activeQuestionnaire}
            defaultActiveKey={INTRODUCTION_TAB}
            onTabChange={handleTabChange}
            tabProps={tabProps}
          />
        )}
        {isPreparingPdf && (
          <QuestionnaireExportSource
            data={results}
            mode={mode}
            questionnaireSlug={activeQuestionnaire}
            questionOptions={questionOptions}
          />
        )}
        {showQuestionNavigation && (
          <nav
            className="loonopzand-dashboard__question-navigation"
            aria-label="Navigatie tussen vragen"
          >
            <button
              type="button"
              onClick={() => selectRelativeQuestion(-1)}
              disabled={!hasPreviousQuestion}
            >
              ← Vorige vraag
            </button>
            <button
              type="button"
              onClick={() => selectRelativeQuestion(1)}
              disabled={!hasNextQuestion}
            >
              Volgende vraag →
            </button>
          </nav>
        )}
      </main>
    </div>
  );
}

export default function LoonOpZandDashboard() {
  return (
    <PdfExportProvider documentTitle="Loon op Zand dashboard">
      <LoonOpZandDashboardContent />
    </PdfExportProvider>
  );
}
